// b14: Inställningar → Båten, för en båt. Den ersätter Motorerna och sidorna från b12 och b13.
//
//   - Förslaget: navet listar motorernas och tankarnas instanser som det hör, och appen föreslår
//     en mall. Ägaren bekräftar med ett tryck, eller väljer en annan mall och ändrar.
//   - Motorerna: namnet, platsen (babord, styrbord, mitten, generatorn eller utombordaren),
//     instansen i NMEA 2000 (tom utan NMEA 2000), för en generator utan NMEA 2000 förbrukningen
//     i l/h, och serviceintervallet.
//   - Tankarna: namnet, sorten och numret enligt PGN 127505, och volymen för tankarna med bränsle.
//   - Matningen: vilka motorer som tar från vilka tankar. Grupperna är det som hänger ihop, och
//     räckvidden räknas per grupp.
//   - Segelbåten, och gränserna för självsvängningen.
//
// Varje ändring står i platsens logg. Under Tankarna visas tabellen per tank, som i b13.
//
// b15: motorerna har en sort (inombordare, utombordare eller generator), och platsen ett nummer
// när flera delar plats (Babord 2). Förbrukarna som inte driver båten, generatorerna och
// värmarna, har förbrukningen i l/h, tanken och signalen för när de går, med den inlärda
// förbrukningen och katalogens förval som förslag. Ingenting sparas förrän ägaren sparar båten.
//
// b16: värmarens signal kan vara "Temperaturen vid utblåset" (utblas.tsx), med kurvan för de
// senaste 24 timmarna.

import {
  arGenerator,
  BRANSLETANK,
  type Dieseltank,
  FORBRUKARE,
  type Forbrukarlage,
  type Forbrukarnyckel,
  grupper,
  MALLAR,
  MALLNAMN,
  type Mallnamn,
  MOTORER,
  MOTORPLATSNAMN,
  MOTORSORTER,
  MOTORSORTNAMN,
  type Motor,
  type Motorsort,
  mall,
  type Platsinstallningar,
  TANKSORTER,
  TANKSORTNAMN,
  type Tanksort,
  tal,
  tankinstans,
  tanksort,
  type Uppstallning,
} from "@batkoll/gemensam";
import { KATALOGEN } from "@koll/katalog";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort } from "../komponenter.tsx";
import {
  Utblasfalt,
  Utblaskurvan,
  type Utblasutkast,
  utblasFran,
  utblasUtkast,
} from "./utblas.tsx";

const lasTal = (s: string) => (s.trim() ? Number(s.trim().replace(",", ".")) : Number.NaN);
const visaTal = (v: number | null | undefined) =>
  v === null || v === undefined ? "" : String(v).replace(".", ",");

/** Ett tal eller null för ett tomt fält. Kastar för text som inte är ett tal. */
function talEllerTomt(s: string, namn: string): number | null {
  if (!s.trim()) return null;
  const v = lasTal(s);
  if (!Number.isFinite(v)) throw new Felsvar(`${namn} ska vara ett tal.`, 400);
  return v;
}

type Spara = (
  sektion: string,
  gor: () => Promise<Platsinstallningar>,
  ok?: string,
) => Promise<boolean>;

/** "Babords tank (diesel)", med tankens namn och instans. */
export function tankrubrik(t: Pick<Dieseltank, "instans" | "namn">): string {
  return `${t.namn} (${t.instans})`;
}

interface Motorutkast {
  id: number;
  plats: Motor;
  sort: Motorsort;
  namn: string;
  instans: string;
  lh: string;
  service: string;
}

interface Tankutkast {
  id: number;
  sort: Tanksort;
  nr: string;
  namn: string;
  volym: string;
}

/**
 * b15: en värmare. Signalen är tom (ingen), en signal i katalogen, eller "ha" med entiteten. b16:
 * eller "utblas", temperaturen vid utblåset.
 */
interface Varmarutkast {
  id: number;
  nyckel: Forbrukarnyckel;
  namn: string;
  lh: string;
  tank: string;
  signal: string;
  entitet: string;
  utblas: Utblasutkast;
}

interface Utkast {
  motorer: Motorutkast[];
  tankar: Tankutkast[];
  varmare: Varmarutkast[];
  /** "motorid/tankid" */
  matning: Set<string>;
  segelbat: boolean;
  mall: Mallnamn | null;
}

let nasta = 1;

/** Utkastet ur en uppställning, med servicen. */
function utkastFor(
  u: Uppstallning,
  service: Partial<Record<Motor, number | null>>,
  volymer: Map<string, number | null>,
): Utkast {
  const motorer = u.motorer.map((m) => ({
    id: nasta++,
    plats: m.plats,
    sort: m.sort,
    namn: m.namn,
    instans: m.instans === null ? "" : String(m.instans),
    lh: visaTal(m.lh),
    service: visaTal(service[m.plats] ?? null),
  }));
  const tankar = u.tankar.map((t) => {
    const s = tanksort(t.instans);
    return {
      id: nasta++,
      sort: s?.sort ?? "diesel",
      nr: String((s?.nr ?? 0) + 1),
      namn: t.namn,
      volym: visaTal(t.volymL ?? volymer.get(t.instans) ?? null),
    };
  });
  const matning = new Set<string>();
  for (const r of u.matning) {
    const m = motorer.find((x) => x.plats === r.motor);
    const t = tankar.find((x) => tankinstans(x.sort, Number(x.nr) - 1) === r.tank);
    if (m && t) matning.add(`${m.id}/${t.id}`);
  }
  const varmare = (u.forbrukare ?? []).map((f) => ({
    id: nasta++,
    nyckel: f.nyckel,
    namn: f.namn,
    lh: visaTal(f.lh),
    tank: f.tank ?? "",
    signal: f.utblas
      ? "utblas"
      : f.signal === null
        ? ""
        : f.signal.startsWith("ha:")
          ? "ha"
          : f.signal,
    entitet: f.signal?.startsWith("ha:") ? f.signal.slice(3) : "",
    utblas: utblasUtkast(f.utblas, "bat"),
  }));
  return { motorer, tankar, varmare, matning, segelbat: u.segelbat, mall: u.mall };
}

const instansFor = (t: Tankutkast) => {
  const nr = Number(t.nr);
  return Number.isInteger(nr) && nr >= 1 && nr <= 16 ? tankinstans(t.sort, nr - 1) : null;
};

/** Uppställningen ur utkastet. Kastar Felsvar för det som inte är tal. */
function uppstallningFor(d: Utkast): {
  u: Uppstallning;
  service: Partial<Record<Motor, number | null>>;
} {
  const service: Partial<Record<Motor, number | null>> = {};
  const motorer = d.motorer.map((m) => {
    const instans = talEllerTomt(m.instans, "Motorns instans");
    if (instans !== null && !Number.isInteger(instans)) {
      throw new Felsvar("Motorns instans ska vara ett heltal, till exempel 0.", 400);
    }
    service[m.plats] = talEllerTomt(m.service, "Serviceintervallet");
    return {
      plats: m.plats,
      sort: m.sort,
      namn: m.namn,
      instans,
      lh: m.sort === "generator" ? talEllerTomt(m.lh, "Förbrukningen") : null,
    };
  });
  const forbrukare = d.varmare.map((v) => {
    const entitet = v.entitet.trim();
    if (v.signal === "ha" && !/^[a-z_]+\.[a-z0-9_]+$/.test(entitet)) {
      throw new Felsvar("Skriv entiteten i Home Assistant, till exempel switch.varmaren.", 400);
    }
    return {
      nyckel: v.nyckel,
      namn: v.namn,
      lh: talEllerTomt(v.lh, "Värmarens förbrukning"),
      tank: v.tank === "" ? null : v.tank,
      signal:
        v.signal === "" || v.signal === "utblas"
          ? null
          : v.signal === "ha"
            ? `ha:${entitet}`
            : v.signal,
      utblas: v.signal === "utblas" ? utblasFran(v.utblas) : null,
    };
  });
  const tankar = d.tankar.map((t) => {
    const instans = instansFor(t);
    if (!instans) throw new Felsvar("Tankens nummer ska vara ett heltal från 1.", 400);
    return { instans, namn: t.namn, volymL: talEllerTomt(t.volym, "Tankens volym") };
  });
  const matning = [...d.matning].flatMap((k) => {
    const [mi, ti] = k.split("/").map(Number);
    const m = d.motorer.find((x) => x.id === mi);
    const t = d.tankar.find((x) => x.id === ti);
    const instans = t ? instansFor(t) : null;
    return m && instans && BRANSLETANK.test(instans) ? [{ motor: m.plats, tank: instans }] : [];
  });
  return {
    u: {
      motorer,
      tankar,
      matning,
      forbrukare,
      segelbat: d.segelbat,
      mall: d.mall,
      bekraftad: true,
    },
    service,
  };
}

/** b13: tankens tabell, med det inlärda och nollställningen. */
function Tabell(props: { t: Dieseltank; plats: string; spara: Spara; kanAndra: boolean }) {
  const { t } = props;
  const [saker, setSaker] = useState(false);
  const tb = t.tabell;
  const volym = t.anvandVolymL;
  const rader = Array.from({ length: 11 }, (_, i) => i * 10);
  return (
    <section class="tanktabell" aria-label={`Tabellen för ${tankrubrik(t).toLowerCase()}`}>
      <h4>{tankrubrik(t)}</h4>
      <p class="liten">
        {tb.text} Tabellen är inlärd ur motorernas förbrukning, inte uppmätt.
        {tb.anvands ? " Den används för litrarna kvar." : " Litrarna kvar räknas linjärt."}
      </p>
      {volym !== null && (
        <table class="tabell">
          <thead>
            <tr>
              <th scope="col">Nivå</th>
              <th scope="col">Liter</th>
              <th scope="col">Linjärt</th>
              <th scope="col">Data</th>
            </tr>
          </thead>
          <tbody>
            {rader.map((n) => {
              const k = n / 5;
              const liter = tb.liter?.[k];
              const data = (tb.inlart[k] ?? false) || (tb.inlart[k - 1] ?? false);
              return (
                <tr key={n}>
                  <td>{n} %</td>
                  <td>{liter === undefined ? "–" : `${tal(liter, 0)} l`}</td>
                  <td>{tal((n / 100) * volym, 0)} l</td>
                  <td>{data ? "inlärd" : "–"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {props.kanAndra &&
        (saker ? (
          <div class="knapprad">
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                void props
                  .spara(
                    "motorer",
                    async () => {
                      await api.nollstallTabell(props.plats, t.instans);
                      return api.installningar(props.plats);
                    },
                    `Tabellen för ${tankrubrik(t).toLowerCase()} är nollställd.`,
                  )
                  .then(() => setSaker(false))
              }
            >
              Ja, nollställ tabellen
            </button>
            <button type="button" class="knapp-andra" onClick={() => setSaker(false)}>
              Avbryt
            </button>
          </div>
        ) : (
          <div class="knapprad">
            <button type="button" class="knapp-andra" onClick={() => setSaker(true)}>
              Nollställ tabellen
            </button>
          </div>
        ))}
    </section>
  );
}

/** Bilden av grupperna: varje grupps motorer och tankar, och var räckvidden räknas. */
function Grupperna(props: { u: Uppstallning }) {
  const g = grupper(props.u).filter((x) => x.motorer.some((m) => !arGenerator(m)));
  const tanknamn = (t: string) => props.u.tankar.find((x) => x.instans === t)?.namn ?? t;
  return (
    <section class="grupperna" aria-label="Grupperna">
      <h3>Grupperna</h3>
      <div class="grupp-rad">
        {grupper(props.u).map((x) => (
          <div class="grupp" key={x.namn}>
            <p class="grupp-motorer">{x.namn}</p>
            <p class="grupp-pil" aria-hidden="true">
              ↓
            </p>
            <p class="grupp-tankar">
              {x.tankar.length === 0 ? "ingen tank" : x.tankar.map(tanknamn).join(", ")}
            </p>
          </div>
        ))}
      </div>
      <p class="liten">
        {g.length <= 1
          ? "En grupp: räckvidden räknas för hela båten, med båtens förbrukningskurva."
          : `${g.length} grupper: räckvidden räknas per grupp, och båtens räckvidd är den kortaste gruppens.`}
        {props.u.segelbat
          ? " Under segling, när ingen motor går, räknas ingen räckvidd med farten nu."
          : ""}
      </p>
    </section>
  );
}

export function Batdel(props: {
  s: Platsinstallningar;
  spara: Spara;
  beskedrad: ComponentChildren;
}) {
  const m = props.s.motorer;
  const volymer = new Map((m?.tankar ?? []).map((t) => [t.instans, t.volymL]));
  const service = Object.fromEntries(
    Object.entries(m?.service ?? {}).map(([k, v]) => [k, v?.intervall ?? null]),
  ) as Partial<Record<Motor, number | null>>;
  const [d, setD] = useState<Utkast | null>(m ? utkastFor(m.uppstallning, service, volymer) : null);
  const [kurs, setKurs] = useState(visaTal(m?.pendling.kursGrader.varde ?? null));
  const [gir, setGir] = useState(visaTal(m?.pendling.girGraderS.varde ?? null));
  const [slagsida, setSlagsida] = useState(visaTal(m?.pendling.slagsidaGrader.varde ?? null));
  if (!m || !d) return null;
  const id = props.s.plats.id;
  const p = m.pendling;
  const installd = p.kursGrader.installd || p.girGraderS.installd || p.slagsidaGrader.installd;
  const tankar = m.tankar ?? [];
  const kanAndra = props.s.plats.roll === "agare";
  const f = m.forslag;
  let bild: Uppstallning = m.uppstallning;
  try {
    bild = uppstallningFor(d).u;
  } catch {
    // Ett fält som inte är ett tal: bilden visar den sparade uppställningen.
  }

  const andraMotor = (mid: number, x: Partial<Motorutkast>) =>
    setD({ ...d, mall: null, motorer: d.motorer.map((y) => (y.id === mid ? { ...y, ...x } : y)) });
  const andraTank = (tid: number, x: Partial<Tankutkast>) =>
    setD({ ...d, mall: null, tankar: d.tankar.map((y) => (y.id === tid ? { ...y, ...x } : y)) });
  const valjMall = (namn: Mallnamn) => {
    const u = mall(namn, f.hort);
    setD({ ...utkastFor(u, service, volymer), mall: namn });
  };

  function spara(e: Event) {
    e.preventDefault();
    void props.spara(
      "motorer",
      async () => {
        const pendling = [kurs, gir, slagsida].map(lasTal);
        if (!pendling.every(Number.isFinite)) {
          throw new Felsvar("Skriv gränserna för självsvängningen som tal.", 400);
        }
        const [kursGrader, girGraderS, slagsidaGrader] = pendling as number[];
        const { u, service: sv } = uppstallningFor(d as Utkast);
        return api.andraPlats(id, {
          motorer: { uppstallning: u, service: sv },
          pendling: { kursGrader, girGraderS, slagsidaGrader },
        });
      },
      "Båten är sparad.",
    );
  }

  const bekrafta = () =>
    void props.spara(
      "motorer",
      () => {
        const u = { ...f.uppstallning, bekraftad: true };
        u.tankar = u.tankar.map((t) => ({ ...t, volymL: volymer.get(t.instans) ?? null }));
        return api.andraPlats(id, { motorer: { uppstallning: u } });
      },
      `Båtens uppställning är bekräftad: ${(MALLNAMN[f.mall] ?? f.mall).toLowerCase()}.`,
    );

  const falt = (
    etikett: string,
    v: string,
    satt: (v: string) => void,
    numerisk?: "numeric" | "decimal",
  ) => (
    <label>
      {etikett}
      <input
        inputMode={numerisk}
        autoComplete="off"
        value={v}
        onInput={(e) => satt(e.currentTarget.value)}
      />
    </label>
  );
  const fria = MOTORER.filter((x) => !d.motorer.some((y) => y.plats === x));
  /** b15: de lediga platserna för en sort: generatorerna har generator, de andra en sida. */
  const friaFor = (sort: Motorsort) =>
    fria.filter((x) => (sort === "generator") === arGenerator(x));
  const andraSort = (x: Motorutkast, sort: Motorsort) => {
    const plats =
      (sort === "generator") === arGenerator(x.plats) ? x.plats : (friaFor(sort)[0] ?? x.plats);
    andraMotor(x.id, { sort, plats });
  };
  const forbrukare = m.forbrukare ?? [];
  const lage = (nyckel: string): Forbrukarlage | undefined =>
    forbrukare.find((x) => x.nyckel === nyckel);
  const varmareForval = KATALOGEN.enhet.find((e) => e.forbrukare?.sort === "varmare");
  const friaVarmare = FORBRUKARE.filter((n) => !d.varmare.some((v) => v.nyckel === n));
  const andraVarmare = (vid: number, x: Partial<Varmarutkast>) =>
    setD({ ...d, varmare: d.varmare.map((y) => (y.id === vid ? { ...y, ...x } : y)) });
  /** Det inlärda och förvalet, med en knapp som fyller i värdet. */
  const forslagFor = (l: Forbrukarlage | undefined, lh: string, satt: (v: string) => void) => {
    const delar: ComponentChildren[] = [];
    if (l?.inlard) {
      const v = l.inlard;
      delar.push(
        <p class="liten" key="inlard">
          Inlärd: {tal(v.lh, 1)} l/h ({v.matningar} {v.matningar === 1 ? "mätning" : "mätningar"})
          {v.foreslas && visaTal(v.lh) !== lh ? (
            <>
              {" "}
              <button type="button" class="knapp-lank" onClick={() => satt(visaTal(v.lh))}>
                Använd {tal(v.lh, 1)} l/h
              </button>
            </>
          ) : v.foreslas ? null : (
            " Förslaget kommer med 3 mätningar."
          )}
        </p>,
      );
    }
    if (l?.forval && lh.trim() === "") {
      const fv = l.forval;
      delar.push(
        <p class="liten" key="forval">
          Förslag: {fv.text}.{" "}
          <button type="button" class="knapp-lank" onClick={() => satt(visaTal(fv.lh))}>
            Använd {tal(fv.lh, 1)} l/h
          </button>
        </p>,
      );
    }
    return delar;
  };
  const bransle = d.tankar.filter((t) => {
    const i = instansFor(t);
    return i !== null && BRANSLETANK.test(i);
  });

  return (
    <Kort rubrik="Båten">
      <section class="forslag" aria-label="Förslaget">
        {!m.uppstallning.bekraftad && (
          <p class="info-rad">
            Uppställningen är inte bekräftad. Kontrollera den nedan, eller bekräfta förslaget.
          </p>
        )}
        <p>{f.text}</p>
        {kanAndra && (
          <div class="knapprad">
            <button type="button" class="knapp-huvud" onClick={bekrafta}>
              Bekräfta förslaget: {(MALLNAMN[f.mall] ?? f.mall).toLowerCase()}
            </button>
          </div>
        )}
        <fieldset class="mallar">
          <legend>Mallarna</legend>
          <div class="knapprad">
            {MALLAR.map((x) => (
              <button
                key={x}
                type="button"
                class="knapp-andra"
                aria-pressed={d.mall === x}
                onClick={() => valjMall(x)}
              >
                {MALLNAMN[x]}
              </button>
            ))}
          </div>
          <p class="liten">
            En mall fyller i motorerna, tankarna och matningen nedan. Ändra det som inte stämmer,
            och spara båten.
          </p>
        </fieldset>
      </section>
      <form class="formular" onSubmit={spara}>
        <fieldset>
          <legend>Motorerna</legend>
          {d.motorer.map((x, i) => (
            <div class="uppstallning-rad" key={x.id}>
              {falt(`Motor ${i + 1}, namn`, x.namn, (v) => andraMotor(x.id, { namn: v }))}
              <label>
                {`Motor ${i + 1}, sort`}
                <select
                  value={x.sort}
                  onChange={(e) => andraSort(x, e.currentTarget.value as Motorsort)}
                >
                  {MOTORSORTER.filter((y) => y === x.sort || friaFor(y).length > 0).map((y) => (
                    <option key={y} value={y}>
                      {MOTORSORTNAMN[y]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {`Motor ${i + 1}, plats`}
                <select
                  value={x.plats}
                  onChange={(e) => andraMotor(x.id, { plats: e.currentTarget.value as Motor })}
                >
                  {MOTORER.filter((y) => y === x.plats || friaFor(x.sort).includes(y)).map((y) => (
                    <option key={y} value={y}>
                      {MOTORPLATSNAMN[y]}
                    </option>
                  ))}
                </select>
              </label>
              {falt(
                `Motor ${i + 1}, instans i NMEA 2000`,
                x.instans,
                (v) => andraMotor(x.id, { instans: v }),
                "numeric",
              )}
              {falt(
                `Motor ${i + 1}, serviceintervall (timmar)`,
                x.service,
                (v) => andraMotor(x.id, { service: v }),
                "decimal",
              )}
              {kanAndra && (
                <button
                  type="button"
                  class="knapp-lank"
                  onClick={() =>
                    setD({
                      ...d,
                      mall: null,
                      motorer: d.motorer.filter((y) => y.id !== x.id),
                      matning: new Set([...d.matning].filter((k) => !k.startsWith(`${x.id}/`))),
                    })
                  }
                >
                  Ta bort {x.namn || `motor ${i + 1}`}
                </button>
              )}
            </div>
          ))}
          {kanAndra && fria.length > 0 && (
            <div class="knapprad">
              <button
                type="button"
                class="knapp-andra"
                onClick={() =>
                  setD({
                    ...d,
                    mall: null,
                    motorer: [
                      ...d.motorer,
                      {
                        id: nasta++,
                        plats: fria[0] as Motor,
                        sort: arGenerator(fria[0] as Motor) ? "generator" : "inombordare",
                        namn: MOTORPLATSNAMN[fria[0] as Motor],
                        instans: "",
                        lh: "",
                        service: "",
                      },
                    ],
                  })
                }
              >
                Lägg till en motor
              </button>
            </div>
          )}
          <p class="liten">
            Instansen är motorns nummer på NMEA 2000 (127488 och 127489). Lämna den tom för en motor
            utan NMEA 2000, till exempel en generator: då räknas den med förbrukningen i l/h när
            generator.gar på MasterBus säger att den går. Ombord visar sudo batkoll-nav status vilka
            instanser navet hör. Flera motorer på samma sida får ett nummer: Babord och Babord 2.
          </p>
        </fieldset>
        <fieldset>
          <legend>Förbrukarna</legend>
          <p class="liten">
            Förbrukare som inte driver båten, men tar bränsle: generatorn och värmaren. Navet lär
            sig förbrukningen när bara en av dem har gått mellan två stabila nivåer i tanken, och
            inga motorer.
          </p>
          {d.motorer
            .filter((x) => x.sort === "generator")
            .map((x) => {
              const l = lage(x.plats);
              return (
                <div class="uppstallning-rad" key={`g${x.id}`}>
                  <p>
                    <strong>{x.namn}</strong>{" "}
                    <span class="liten">
                      {x.instans.trim() !== ""
                        ? `bränsleflödet på NMEA 2000, instans ${x.instans}`
                        : x.plats === "generator"
                          ? "går enligt generator.gar på MasterBus"
                          : "ingen signal: räknas inte"}
                    </span>
                  </p>
                  {x.instans.trim() === "" &&
                    falt(
                      `${x.namn}, förbrukning (l/h)`,
                      x.lh,
                      (v) => andraMotor(x.id, { lh: v }),
                      "decimal",
                    )}
                  {x.instans.trim() === "" &&
                    forslagFor(l, x.lh, (v) => andraMotor(x.id, { lh: v }))}
                </div>
              );
            })}
          {d.varmare.map((v, i) => {
            const l = lage(v.nyckel);
            return (
              <div class="uppstallning-rad" key={`v${v.id}`}>
                {falt(`Värmare ${i + 1}, namn`, v.namn, (x) => andraVarmare(v.id, { namn: x }))}
                {falt(
                  `${v.namn || `Värmare ${i + 1}`}, förbrukning (l/h)`,
                  v.lh,
                  (x) => andraVarmare(v.id, { lh: x }),
                  "decimal",
                )}
                {forslagFor(l, v.lh, (x) => andraVarmare(v.id, { lh: x }))}
                <label>
                  {`${v.namn || `Värmare ${i + 1}`}, tanken`}
                  <select
                    value={v.tank}
                    onChange={(e) => andraVarmare(v.id, { tank: e.currentTarget.value })}
                  >
                    <option value="">Ingen tank</option>
                    {bransle.map((t) => {
                      const ti = instansFor(t) as string;
                      return (
                        <option key={t.id} value={ti}>
                          {t.namn}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  {`${v.namn || `Värmare ${i + 1}`}, signalen för när den går`}
                  <select
                    value={v.signal}
                    onChange={(e) => andraVarmare(v.id, { signal: e.currentTarget.value })}
                  >
                    <option value="">Ingen signal: värmaren räknas inte</option>
                    {(m.gangsignaler ?? []).map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.namn} ({g.id})
                      </option>
                    ))}
                    <option value="ha">En entitet i Home Assistant</option>
                    <option value="utblas">Temperaturen vid utblåset</option>
                  </select>
                </label>
                {v.signal === "utblas" && (
                  <Utblasfalt
                    namn={v.namn || `Värmare ${i + 1}`}
                    d={v.utblas}
                    satt={(u) => andraVarmare(v.id, { utblas: u })}
                    s={props.s}
                  />
                )}
                {m.uppstallning.forbrukare.find((x) => x.nyckel === v.nyckel)?.utblas && (
                  <Utblaskurvan platsId={id} nyckel={v.nyckel} nu={Date.now()} />
                )}
                {v.signal === "ha" &&
                  falt(
                    `${v.namn || `Värmare ${i + 1}`}, entiteten i Home Assistant`,
                    v.entitet,
                    (x) => andraVarmare(v.id, { entitet: x }),
                  )}
                {kanAndra && (
                  <button
                    type="button"
                    class="knapp-lank"
                    onClick={() => setD({ ...d, varmare: d.varmare.filter((y) => y.id !== v.id) })}
                  >
                    Ta bort {v.namn || `värmare ${i + 1}`}
                  </button>
                )}
              </div>
            );
          })}
          {kanAndra && friaVarmare.length > 0 && (
            <div class="knapprad">
              <button
                type="button"
                class="knapp-andra"
                onClick={() =>
                  setD({
                    ...d,
                    varmare: [
                      ...d.varmare,
                      {
                        id: nasta++,
                        nyckel: friaVarmare[0] as Forbrukarnyckel,
                        namn:
                          d.varmare.length === 0 ? "Värmaren" : `Värmare ${d.varmare.length + 1}`,
                        lh: "",
                        tank: bransle[0] ? (instansFor(bransle[0]) ?? "") : "",
                        signal: "",
                        entitet: "",
                        utblas: utblasUtkast(null, "bat"),
                      },
                    ],
                  })
                }
              >
                Lägg till en värmare
              </button>
            </div>
          )}
          {d.varmare.length === 0 && varmareForval?.forbrukare && (
            <p class="liten">
              Förslag: {varmareForval.namn}, cirka {tal(varmareForval.forbrukare.lh, 1)} l/h. Utan
              en signal räknas värmaren inte.
            </p>
          )}
        </fieldset>
        <fieldset>
          <legend>Tankarna</legend>
          {d.tankar.map((t, i) => {
            const instans = instansFor(t);
            const kap = tankar.find((x) => x.instans === instans)?.kapacitetL ?? null;
            const arBransle = instans !== null && BRANSLETANK.test(instans);
            return (
              <div class="uppstallning-rad" key={t.id}>
                {falt(`Tank ${i + 1}, namn`, t.namn, (v) => andraTank(t.id, { namn: v }))}
                <label>
                  {`Tank ${i + 1}, sort`}
                  <select
                    value={t.sort}
                    onChange={(e) => andraTank(t.id, { sort: e.currentTarget.value as Tanksort })}
                  >
                    {TANKSORTER.map((y) => (
                      <option key={y} value={y}>
                        {TANKSORTNAMN[y]}
                      </option>
                    ))}
                  </select>
                </label>
                {falt(`Tank ${i + 1}, nummer`, t.nr, (v) => andraTank(t.id, { nr: v }), "numeric")}
                {arBransle &&
                  falt(
                    `Tank ${i + 1}, volym (liter)`,
                    t.volym,
                    (v) => andraTank(t.id, { volym: v }),
                    "decimal",
                  )}
                {arBransle && t.volym.trim() === "" && kap !== null && (
                  <p class="liten">
                    Tankgivaren säger {tal(kap, 0)} liter, och den används tills du ställer in en
                    egen.{" "}
                    <button
                      type="button"
                      class="knapp-lank"
                      onClick={() => andraTank(t.id, { volym: visaTal(kap) })}
                    >
                      Använd {tal(kap, 0)} liter
                    </button>
                  </p>
                )}
                {kanAndra && (
                  <button
                    type="button"
                    class="knapp-lank"
                    onClick={() =>
                      setD({
                        ...d,
                        mall: null,
                        tankar: d.tankar.filter((y) => y.id !== t.id),
                        matning: new Set([...d.matning].filter((k) => !k.endsWith(`/${t.id}`))),
                      })
                    }
                  >
                    Ta bort {t.namn || `tank ${i + 1}`}
                  </button>
                )}
              </div>
            );
          })}
          {kanAndra && (
            <div class="knapprad">
              <button
                type="button"
                class="knapp-andra"
                onClick={() => {
                  const nr = (sort: Tanksort) => d.tankar.filter((x) => x.sort === sort).length + 1;
                  setD({
                    ...d,
                    mall: null,
                    tankar: [
                      ...d.tankar,
                      {
                        id: nasta++,
                        sort: "diesel",
                        nr: String(nr("diesel")),
                        namn: `Tank ${d.tankar.length + 1}`,
                        volym: "",
                      },
                    ],
                  });
                }}
              >
                Lägg till en tank
              </button>
            </div>
          )}
          <p class="liten">
            Sorten och numret är tankens i PGN 127505: diesel nummer 1 är tanken diesel, nummer 2
            diesel2. Utan volym visas ingen räckvidd för tanken. Ditt värde går före tankgivarens.
          </p>
        </fieldset>
        <fieldset>
          <legend>Matningen</legend>
          {bransle.length === 0 || d.motorer.length === 0 ? (
            <p class="liten">Lägg till en motor och en tank med bränsle.</p>
          ) : (
            <table class="tabell matning">
              <thead>
                <tr>
                  <th scope="col">Motor</th>
                  {bransle.map((t) => (
                    <th scope="col" key={t.id}>
                      {t.namn}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.motorer.map((x) => (
                  <tr key={x.id}>
                    <th scope="row">{x.namn}</th>
                    {bransle.map((t) => {
                      const k = `${x.id}/${t.id}`;
                      return (
                        <td key={t.id}>
                          <input
                            type="checkbox"
                            aria-label={`${x.namn} tar från ${t.namn}`}
                            checked={d.matning.has(k)}
                            onChange={(e) => {
                              const ny = new Set(d.matning);
                              if (e.currentTarget.checked) ny.add(k);
                              else ny.delete(k);
                              setD({ ...d, mall: null, matning: ny });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p class="liten">
            Kryssa i vilka tankar varje motor tar från. Flera motorer kan ta från samma tank: då
            räknas tankens form med summan av deras förbrukning.
          </p>
        </fieldset>
        <Grupperna u={bild} />
        <label class="kryss">
          <input
            type="checkbox"
            checked={d.segelbat}
            onChange={(e) => setD({ ...d, mall: null, segelbat: e.currentTarget.checked })}
          />
          Segelbåt: ingen räckvidd med farten nu under segling
        </label>
        <fieldset>
          <legend>Självsvängningen med autopiloten på</legend>
          {falt("Kursens avvikelse (°)", kurs, setKurs, "decimal")}
          {falt("Girhastigheten (°/s)", gir, setGir, "decimal")}
          {falt("Slagsidan (°)", slagsida, setSlagsida, "decimal")}
          <p class="liten">
            Info när någon av dem svänger med en jämn period på 3–20 sekunder, minst så här mycket,
            i en minut. Högst en gång per resa. Förval: {visaTal(p.kursGrader.forval)}°,{" "}
            {visaTal(p.girGraderS.forval)} °/s och {visaTal(p.slagsidaGrader.forval)}°
            {installd ? ". Nu inställda." : "."} Båtkoll ändrar aldrig något i autopiloten eller
            Humphree.
          </p>
        </fieldset>
        {props.beskedrad}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara båten
          </button>
          {installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara(
                  "motorer",
                  () =>
                    api.andraPlats(id, {
                      pendling: { kursGrader: null, girGraderS: null, slagsidaGrader: null },
                    }),
                  "Gränserna för självsvängningen är återställda till förvalen.",
                )
              }
            >
              Återställ självsvängningen till förvalen
            </button>
          )}
        </div>
      </form>
      {tankar.length > 0 && (
        <div class="tankarna">
          <h3>Tankarna med bränsle</h3>
          <p class="liten">
            Navet lär sig hur nivån hänger ihop med liter: ur stabila nivåer före och efter resor
            och det som motorerna förbrukade mellan dem, och ur tankningarna du skriver in under
            Resor. Där data saknas är tabellen linjär.
          </p>
          {tankar.map((t) => (
            <Tabell key={t.instans} t={t} plats={id} spara={props.spara} kanAndra={kanAndra} />
          ))}
        </div>
      )}
    </Kort>
  );
}
